<div class="upload-item"
  [class.queued]="upload().status === 'queued'"
  [class.active]="isActive()"
  [class.error]="hasError()"
  [class.done]="upload().status === 'done'"
  [class.canceled]="upload().status === 'canceled'">
  
  <!-- Top Row: File Icon & Info -->
  <div class="upload-header">
    <div class="file-icon">
      <fa-duotone-icon [icon]="getFileIcon()"></fa-duotone-icon>
    </div>
    
    <div class="file-details">
      <div class="file-name" [pTooltip]="upload().name" tooltipPosition="top">
        {{ upload().name }}
      </div>
      <div class="file-meta">
        <span class="file-size">{{ formatSize(upload().size) }}</span>
        @if (upload().status === 'done' && getUploadTime() > 0) {
          <span class="separator">•</span>
          <span class="upload-time">{{ formatDuration(getUploadTime()) }}</span>
        }
        @if (upload().width && upload().height) {
          <span class="separator">•</span>
          <span class="dimensions">{{ upload().width }}×{{ upload().height }}</span>
        }
      </div>
    </div>
  </div>

  <!-- Bottom Row: Progress/Status & Actions -->
  <div class="upload-footer">
    <div class="upload-progress">
      @switch (upload().status) {
      @case ('progress') {
        <div class="progress-info">
          <span class="progress-text">{{ upload().progress }}%</span>
          <span class="progress-size">
            {{ formatSize(upload().bytesUploaded) }} / {{ formatSize(upload().size) }}
          </span>
        </div>
        <p-progressBar 
          [value]="upload().progress" 
          [showValue]="false"
          [style]="{'height': '4px'}">
        </p-progressBar>
      }
      @case ('queued') {
        <div class="status-text">
          <fa-duotone-icon [icon]="faSpinner" class="fa-spin mr-1"></fa-duotone-icon>
          {{ 'upload.queued' | translate }}
        </div>
      }
      @case ('done') {
        <div class="status-text success">
          <fa-duotone-icon [icon]="faCheck" class="mr-1"></fa-duotone-icon>
          {{ 'upload.completed' | translate }}
        </div>
      }
      @case ('failed') {
        <div class="status-text error">
          <fa-duotone-icon [icon]="faExclamationTriangle" class="mr-1"></fa-duotone-icon>
          @if (upload().refusedForStorage) {
            <!-- The lead alone until the plan read lands (the tail is null then); the ending follows the plan and the
                 reader's standing once a storage row stands. -->
            {{ 'upload.storageFullLead' | translate }}
            @if (storageTail(); as tail) {
              {{ tail | translate }}
            }
          } @else {
            {{ getErrorMessage() | translate }}
          }
        </div>
      }
      @case ('alert') {
        <div class="status-text warning">
          <fa-duotone-icon [icon]="faExclamationTriangle" class="mr-1"></fa-duotone-icon>
          {{ getErrorMessage() | translate }}
        </div>
      }
      @case ('resumable') {
        <div class="progress-info">
          <span class="progress-text paused">{{ 'upload.paused' | translate }}</span>
          <span class="progress-size">
            {{ formatSize(upload().bytesUploaded) }} / {{ formatSize(upload().size) }}
          </span>
        </div>
        <p-progressBar
          [value]="getResumeProgress()"
          [showValue]="false"
          [style]="{'height': '4px'}">
        </p-progressBar>
      }
      @case ('canceled') {
        <div class="status-text canceled">
          <fa-duotone-icon [icon]="faXmark" class="mr-1"></fa-duotone-icon>
          {{ 'upload.canceled' | translate }}
          @if (upload().bytesUploaded > 0) {
            <span class="canceled-progress">
              ({{ formatSize(upload().bytesUploaded) }} / {{ formatSize(upload().size) }})
            </span>
          }
        </div>
      }
    }
    </div>
    <!-- Actions -->
    <div class="upload-actions">
    @switch (upload().status) {
      @case ('progress') {
        <button pButton 
          type="button" 
          class="p-button-text p-button-rounded p-button-sm"
          (click)="cancel()"
          [pTooltip]="'upload.cancel' | translate"
          class="p-button-text p-button-rounded p-button-sm">
          <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
        </button>
      }
      @case ('queued') {
        <button pButton 
          type="button" 
          class="p-button-text p-button-rounded p-button-sm"
          (click)="cancel()"
          [pTooltip]="'upload.cancel' | translate"
          class="p-button-text p-button-rounded p-button-sm">
          <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
        </button>
      }
      @case ('alert') {
        <button pButton type="button" class="p-button-text p-button-rounded p-button-sm"
          (click)="rename()" [pTooltip]="'upload.rename' | translate">
          <fa-duotone-icon [icon]="faRotate"></fa-duotone-icon>
        </button>
        <button pButton type="button" class="p-button-text p-button-rounded p-button-sm"
          (click)="uploadNewVersion()" [pTooltip]="'upload.newVersion' | translate">
          <fa-duotone-icon [icon]="faCodeBranch"></fa-duotone-icon>
        </button>
        <button pButton type="button" class="p-button-text p-button-rounded p-button-sm"
          (click)="cancel()" [pTooltip]="'upload.cancel' | translate">
          <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
        </button>
      }
      @case ('failed') {
        <button pButton type="button" class="p-button-text p-button-rounded p-button-sm"
          (click)="cancel()" [pTooltip]="'upload.remove' | translate">
          <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
        </button>
      }
      @case ('resumable') {
        <input #resumeFileInput type="file" class="hidden-file-input"
          (change)="onResumeFileChange($event)" />
        <button pButton type="button" class="p-button-text p-button-rounded p-button-sm"
          (click)="resumeFileInput.click()" [pTooltip]="'upload.resume' | translate">
          <fa-duotone-icon [icon]="faPlay"></fa-duotone-icon>
        </button>
        <button pButton type="button" class="p-button-text p-button-rounded p-button-sm"
          (click)="cancel()" [pTooltip]="'upload.cancel' | translate">
          <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
        </button>
      }
      @case ('canceled') {
        <button pButton type="button" class="p-button-text p-button-rounded p-button-sm"
          (click)="remove()" [pTooltip]="'upload.remove' | translate">
          <fa-duotone-icon [icon]="faTrash"></fa-duotone-icon>
        </button>
      }
    }
  </div>
  </div>
</div>